/**
 * Normalize enrich-clipboard / highlight AI cost payloads for shape meta + hover.
 * @param {object|null|undefined} raw
 * @returns {{ estimatedCostUsd: number, totalTokenCount: number, parts?: object[] }|null}
 */
export function normalizeAiCost(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const estimatedCostUsd = Math.max(0, Number(raw.estimatedCostUsd ?? raw.aiCostUsd) || 0);
  const totalTokenCount = Math.max(
    0,
    Math.round(Number(raw.totalTokenCount ?? raw.aiTokens) || 0)
  );
  if (!estimatedCostUsd && !totalTokenCount) return null;
  const parts = Array.isArray(raw.parts)
    ? raw.parts
      .map((part) => ({
        label: String(part?.label || '').slice(0, 64),
        estimatedCostUsd: Math.max(0, Number(part?.estimatedCostUsd) || 0),
        totalTokenCount: Math.max(0, Math.round(Number(part?.totalTokenCount) || 0)),
      }))
      .filter(p => p.label || p.estimatedCostUsd || p.totalTokenCount)
      .slice(0, 12)
    : undefined;
  return {
    estimatedCostUsd,
    totalTokenCount,
    ...(parts?.length ? { parts } : {}),
  };
}

export function mergeAiCostIntoMeta(meta = {}, nextCost = null) {
  const prev = normalizeAiCost({
    estimatedCostUsd: meta?.aiCostUsd,
    totalTokenCount: meta?.aiTokens,
    parts: meta?.aiCostParts,
  });
  const incoming = normalizeAiCost(nextCost);
  if (!prev && !incoming) return {};
  const estimatedCostUsd = (prev?.estimatedCostUsd || 0) + (incoming?.estimatedCostUsd || 0);
  const totalTokenCount = (prev?.totalTokenCount || 0) + (incoming?.totalTokenCount || 0);
  const parts = [
    ...(prev?.parts || []),
    ...(incoming?.parts || []),
  ].slice(0, 12);
  return {
    aiCostUsd: Number(estimatedCostUsd.toFixed(8)),
    aiTokens: totalTokenCount,
    ...(parts.length ? { aiCostParts: parts } : {}),
  };
}

export function formatAiCostUsd(usd) {
  const n = Number(usd) || 0;
  if (n <= 0) return '$0';
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
}

export function formatAiCostLabel(cost) {
  const normalized = normalizeAiCost(cost);
  if (!normalized) return '';
  const tokens = normalized.totalTokenCount
    ? `${normalized.totalTokenCount.toLocaleString()} tok`
    : '';
  const money = formatAiCostUsd(normalized.estimatedCostUsd);
  if (money && tokens) return `${money} · ${tokens}`;
  return money || tokens;
}

/**
 * Dev hover extras for labeled cost parts (intent-summary, ocr, …).
 * Falls back to a single "AI cost" row when parts are missing.
 * @returns {Array<{ label: string, text: string }>}
 */
export function formatAiCostExtras(cost) {
  const normalized = normalizeAiCost(cost);
  if (!normalized) return [];
  const parts = Array.isArray(normalized.parts) ? normalized.parts : [];
  const partExtras = parts
    .map((part) => {
      const text = formatAiCostLabel(part);
      if (!text) return null;
      const label = String(part.label || 'ai').trim() || 'ai';
      return { label: `AI · ${label}`, text };
    })
    .filter(Boolean);
  if (partExtras.length) return partExtras;
  const total = formatAiCostLabel(normalized);
  return total ? [{ label: 'AI cost', text: total }] : [];
}
